import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  TextInput,
  Dimensions,
  Modal,
  TouchableWithoutFeedback
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import {styles} from './styles';
import RenderCartHeader from './renderCartHeader';
import ToteRowItemsControllers from './toteRowItemsControllers';
import I18n from 'i18n-js';
import {useDispatch, useSelector} from 'react-redux';
import {
  addCart,
  deleteCart,
  fetchCarts,
  resetCartAssignments,
  updateCart,
  updateCartDataLocal
} from '../../actions/scanToCartActions';
import {ActivityIndicator} from 'react-native-web';
import CartErrorPrompt from './cartErrorPrompt';
import DimensionsRow from './dimensionsRow';
import Toast, { useToast } from '../../commons/Toast';
import {ZoneAccessModal} from '../orders/orderPriorityList/zoneAccessModal';
import {UpdateScanPackSettings} from '../../actions/userAction';
import {LinearGradient} from 'expo-linear-gradient';
import {
  getTooltipLeft,
  getTooltipTop,
  getTooltipArrowLeft,
  prepareScanPackSettingsPayload,
  getToggledZoneAccessSource,
  measureIconPosition
} from '../../utils/zoneAccessUtils';
import {
  selectScanPackSettings,
  selectZoneAccessSource,
  selectCanEditScanningPrefs
} from '../../selectors/scanPackSelectors';
import {AntDesign} from '@expo/vector-icons';
import {useNavigation} from '@react-navigation/native';
import UnsavedChangesBar from '../../commons/unsavedChanges/UnsavedChangesBar';
import UnsavedChangesPrompt from '../../commons/unsavedChanges/UnsavedChangesPrompt';
import useUnsavedChanges from '../../commons/unsavedChanges/useUnsavedChanges';

// The backend refuses to edit or delete a cart while any order is assigned to its totes.
const countAssignedOrders = cart => {
  const orderIds = new Set();
  (cart?.cart_rows || []).forEach(row =>
    (row.cart_totes || []).forEach(tote =>
      (tote.assign_order_to_totes || []).forEach(assignment => orderIds.add(assignment.order_id))
    )
  );
  return orderIds.size;
};

const ScanToCartSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width
  );
  const [editingCartId, setEditingCartId] = useState(null);
  const [errorCartId, setErrorCartId] = useState(null);
  const [blockedCart, setBlockedCart] = useState(null);
  // Counts from the last saved state, so a locally removed row can't hide its assignments.
  const assignedOrderCounts = useRef({});
  const { showToast, toastMessage, showToastMessage, hideToast } = useToast();
  const [isToastError, setIsToastError] = useState(false);
  const carts = useSelector(state => state.scanToCart.carts);
  const [showZoneAccessModal, setShowZoneAccessModal] = useState(false);
  const [selectedCartId, setSelectedCartId] = useState(null);
  const [selectedCartName, setSelectedCartName] = useState(null);
  const [showZonePreferenceTooltip, setShowZonePreferenceTooltip] = useState(false);
  const [zonePreferenceIconPosition, setZonePreferenceIconPosition] = useState({x: 0, y: 0, width: 0, centerX: 0});
  const zonePreferenceIconRef = useRef(null);
  const [isUpdatingZonePreference, setIsUpdatingZonePreference] = useState(false);

  const scanPackSettings = useSelector(selectScanPackSettings);
  const zoneAccessSource = useSelector(selectZoneAccessSource);
  const canEditScanningPrefs = useSelector(selectCanEditScanningPrefs);

  useEffect(() => {
    carts.forEach(cart => {
      if (cart.id !== editingCartId) {
        assignedOrderCounts.current[cart.id] = countAssignedOrders(cart);
      }
    });
  }, [carts, editingCartId]);

  const blockIfAssigned = (cartId, action) => {
    const count = assignedOrderCounts.current[cartId] || 0;
    if (count === 0) return false;
    setBlockedCart({id: cartId, count, action});
    return true;
  };

  useEffect(() => {
    const updateWidth = () => {
      setScreenWidth(Dimensions.get('window').width);
    };

    Dimensions.addEventListener('change', updateWidth);
    return () => {
      if (Dimensions.removeEventListener) {
        Dimensions.removeEventListener('change', updateWidth);
      }
    };
  }, []);

  const callBack = () => {
    setIsLoading(false);
  };

  const handleZonePreferenceTooltipToggle = () => {
    measureIconPosition(zonePreferenceIconRef, (position) => {
      setZonePreferenceIconPosition(position);
    });
    setShowZonePreferenceTooltip(!showZonePreferenceTooltip);
  };


  const handleZonePreferenceToggle = () => {
    if (!scanPackSettings || isUpdatingZonePreference || !canEditScanningPrefs) return;

    const newZoneAccessSource = getToggledZoneAccessSource(zoneAccessSource);
    const payload = prepareScanPackSettingsPayload(scanPackSettings, newZoneAccessSource);

    setIsUpdatingZonePreference(true);

    const onComplete = (result) => {
      setIsUpdatingZonePreference(false);
      if (result && result.success === false) {
        setIsToastError(true);
        showToastMessage(
          result.errorMessages?.[0] || I18n.t('scanPack.settingUpdateFailed')
        );
      }
    };

    dispatch(UpdateScanPackSettings(payload, onComplete));
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchCarts(callBack));
  }, []);

  const getNextCartId = () => {
    if (carts.length === 0) return 'C01';
    const cartNumbers = carts.map(cart => {
      const matches = cart.cart_id.match(/C(\d+)/);
      return matches ? parseInt(matches[1], 10) : 0;
    });

    const nextNum = Math.max(...cartNumbers) + 1;
    return `C${nextNum.toString().padStart(2, '0')}`;
  };

  const getNextCartName = () => {
    if (carts.length === 0) return 'Cart 1';
    const cartNumbers = carts.map(cart => {
      const matches = cart.cart_name.match(/Cart (\d+)/);
      return matches ? parseInt(matches[1], 10) : 0;
    });

    const nextNum = Math.max(...cartNumbers) + 1;
    return `Cart ${nextNum}`;
  };

  const getNextRowId = rows => {
    if (rows.length === 0) return 'A';
    const lastRow = rows[rows.length - 1].row_name;
    if (lastRow && lastRow.length === 1 && lastRow >= 'A' && lastRow <= 'Z') {
      return String.fromCharCode(lastRow.charCodeAt(0) + 1);
    }
    return String.fromCharCode('A'.charCodeAt(0) + rows.length);
  };

  const deleteRow = (cartId, rowIndex) => {
    const cart = carts.find(c => c.id === cartId);
    if (cart) {
      if (cart.cart_rows.length === 1) {
        if (blockIfAssigned(cart.id, 'delete')) return;
        dispatch(deleteCart(cart.id, handleCartError));
      } else {
        const updatedCart = {
          ...cart,
          cart_rows: cart.cart_rows.filter((_, index) => index !== rowIndex)
        };
        dispatch(updateCartDataLocal(updatedCart));
        setEditingCartId(cartId);
      }
    }
  };

  const duplicateCart = (originalCart) => {
    dispatch(addCart({
      ...originalCart,
      cart_id: getNextCartId(),
      cart_name: getNextCartName()
    }));
  };

  const duplicateRow = (cartId, rowIndex) => {
    const cart = carts.find(c => c.id === cartId);
    const rowToDuplicate = cart.cart_rows[rowIndex];
    dispatch(updateCartDataLocal({
      ...cart,
      cart_rows: [
        ...cart.cart_rows,
        {
          ...rowToDuplicate,
          row_name: getNextRowId(cart.cart_rows),
          cart_id: cartId
        }
      ]
    }));
    setEditingCartId(cartId);
  };

  const addNewCart = () => {
    const newCart = {
      cart_id: getNextCartId(),
      cart_name: getNextCartName(),
      number_of_totes: 5,
      cart_rows: [
        {
          row_name: 'A',
          row_count: 5,
          default_height: 4,
          default_width: 4,
          default_length: 4
        }
      ]
    };

    dispatch(addCart(newCart));
  };

  const addRow = cartId => {
    const cart = carts.find(c => c.id === cartId);
    if (cart) {
      const updatedCart = {
        ...cart,
        cart_rows: [
          ...cart.cart_rows,
          {
            cart_id: cartId,
            row_name: getNextRowId(cart.cart_rows),
            row_count: 5,
            default_height: 4,
            default_width: 4,
            default_length: 4
          }
        ]
      };
      dispatch(updateCartDataLocal(updatedCart));
      setEditingCartId(cartId);
    }
  };

  const updateRowName = (cartId, rowIndex, newName) => {
    const cart = carts.find(c => c.id === cartId);
    if (cart) {
      const updatedCart = {
        ...cart,
        cart_rows: cart.cart_rows.map((row, index) =>
          index === rowIndex ? {...row, row_name: newName} : row
        )
      };
      dispatch(updateCartDataLocal(updatedCart));
      setEditingCartId(cartId);
    }
  };

  const updateToteCount = (cartId, rowIndex, newValue) => {
    const numValue = parseInt(newValue, 10);
    if (isNaN(numValue)) return;

    const cart = carts.find(c => c.id === cartId);
    if (cart) {
      const newTotes = Math.max(1, Math.min(10, numValue));
      const updatedCart = {
        ...cart,
        cart_rows: cart.cart_rows.map((row, index) =>
          index === rowIndex ? {...row, row_count: newTotes} : row
        )
      };
      dispatch(updateCartDataLocal(updatedCart));
      setEditingCartId(cartId);
    }
  };

  const navigation = useNavigation();
  const hasUnsavedChanges = editingCartId !== null;

  const handleCartUpdate = cartId => {
    const cart = carts.find(c => c.id === cartId);
    if (cart) {
      if (blockIfAssigned(cartId, 'update')) return;
      dispatch(updateCart(cartId, cart, handleCartError));
      setEditingCartId(null);
    }
  };

  const unsavedChanges = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: () => handleCartUpdate(editingCartId),
    onDiscard: () => setEditingCartId(null),
    navigation
  });

  const handleCartError = cartId => {
    if (cartId === 'Cart Deleted') {
      setIsLoading(true);
      dispatch(fetchCarts(callBack));
      return;
    }
    setErrorCartId(cartId);
  };

  // After a successful reset, finish what the user asked for.
  const resetBlockedCart = async () => {
    const {id, action} = blockedCart;
    const cart = carts.find(c => c.id === id);
    const result = await dispatch(resetCartAssignments(cart.cart_id));
    if (!result.ok) return result;

    assignedOrderCounts.current[id] = 0;
    setBlockedCart(null);
    if (action === 'delete') {
      dispatch(deleteCart(id, handleCartError));
    } else {
      dispatch(updateCart(id, cart, handleCartError));
      setEditingCartId(null);
    }
    return result;
  };

  const copyToteId = async (cartId, rowId, toteNumber) => {
    const barcodeValue = `${rowId}-${toteNumber}-${cartId}`;
    await Clipboard.setStringAsync(barcodeValue);
    setIsToastError(false);
    showToastMessage(I18n.t('copiedText.copiedToteId', {toteId: barcodeValue}));
  };

  const renderTote = ({item: toteNumber, rowId, cartId, totalTotes}) => {
    const columnWidth = screenWidth * (screenWidth > 1200 ? 0.38 : 0.48);
    const availableWidth = columnWidth - 20;
    const toteWidth = Math.max(availableWidth / totalTotes - 10, 60);
    return (
      <TouchableOpacity
        testID="copyToteButtonId"
        style={[styles.tote, {width: toteWidth, maxWidth: toteWidth}]}
        onPress={() => copyToteId(cartId, rowId, toteNumber)}>
        <Text
          numberOfLines={1}
          style={[styles.toteText, {fontSize: screenWidth < 768 ? 12 : 14}]}>
          {`${rowId}-${toteNumber}`}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderRow = ({item: row, index, cartId}) => {
    const totesToRender = screenWidth < 900 ? 1 : row.row_count;
    const dimensions = {
      default_length: row?.default_length || 0,
      default_width: row?.default_width || 0,
      default_height: row?.default_height || 0
    };

    const updateDimensions = newDimensions => {
      const cart = carts.find(c => c.id === row.cart_id);
      if (cart) {
        const updatedCart = {
          ...cart,
          cart_rows: cart.cart_rows.map((r, idx) =>
            idx === index ? {...r, 
              default_length: newDimensions.default_length,
              default_width: newDimensions.default_width,
              default_height: newDimensions.default_height
            } : r
          )
        };
        dispatch(updateCartDataLocal(updatedCart));
        setEditingCartId(row.cart_id);
      }
    };

    return (
      <View style={styles.rowContainer}>
        <View style={[styles.rowIdColumn, styles.rowIdColumnResponsive]}>
          <View style={styles.rowControlsContainer}>
            <TouchableOpacity
              testID='duplicateCartRowButton'
              onPress={() => duplicateRow(row.cart_id, index)}
              style={styles.buttonFlexShrink}>
              <i
                className="icon-docs"
                style={{
                  color: '#aad9fc',
                  fontSize: 16,
                }}
                title={I18n.t('scanToCart.duplicateRow')}>
              </i>
            </TouchableOpacity>
            <TouchableOpacity
              testID="deleteCartRowButton"
              style={[styles.deleteButton, styles.buttonFlexShrink]}
              onPress={() => deleteRow(row.cart_id, index)}>
              <i
                className="icon-cancel"
                style={{fontSize: 24, color: '#ffff'}}
              />
            </TouchableOpacity>

            <TextInput
              testID="rowIdInput"
              style={[styles.rowInput, styles.inputFlexible]}
              autoFocus={true}
              value={row.row_name}
              onChangeText={newName =>
                updateRowName(row.cart_id, index, newName)
              }
              autoCapitalize="characters"
            />
          </View>
        </View>

        <View style={[styles.orderInfoColumn, styles.orderInfoColumnResponsive]}>
          <View style={[styles.toteListContainer]}>
            {[...Array(totesToRender)].map((_, i) => (
              <View key={`${row.id}-${i + 1}`}>
                {renderTote({
                  item: i + 1,
                  rowId: row.row_name,
                  cartId,
                  totalTotes: row.row_count
                })}
              </View>
            ))}
          </View>
        </View>

        <View style={[styles.totesRowColumn, styles.totesRowColumnResponsive]}>
          <ToteRowItemsControllers
            initialValue={row.row_count}
            onUpdate={newValue => updateToteCount(row.cart_id, index, newValue)}
          />
        </View>

        {screenWidth > 900 && (
          <View style={[styles.dimensionsColumn, styles.dimensionsColumnResponsive]}>
            <DimensionsRow
              dimensions={dimensions}
              onUpdate={updateDimensions}
            />
          </View>
        )}
      </View>
    );
  };
  const renderCart = ({item: cart}) => (
    <View style={styles.cartContainer}>
      <RenderCartHeader
        name={cart?.cart_name}
        screenWidth={screenWidth}
        id={cart.cart_id}
        cartId={cart.cart_id}
        cart={cart}
        onDuplicateCart={duplicateCart}
      />

      <FlatList
        data={cart.cart_rows}
        renderItem={({item, index}) =>
          renderRow({item, index, cartId: cart.cart_id})
        }
        keyExtractor={(item, index) => `${cart.id}-${item.id}-${index}`}
      />

      <View style={styles.cartFooter}>
        {editingCartId !== cart.id ? (
          <TouchableOpacity
            testID="addCartRowButton"
            style={styles.addRowButton}
            onPress={() => addRow(cart.id)}>
            <i className="icon-plus" style={{color: '#ffff', fontSize: 18}}></i>
            <Text
              style={[
                styles.addRowText,
                {fontSize: screenWidth < 768 ? 12 : 14}
              ]}>
              {I18n.t('scanToCart.addRowLabel')}
            </Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            testID="saveChanges"
            style={styles.addRowButton}
            onPress={() => handleCartUpdate(cart.id)}>
            <Text
              style={[
                styles.addRowText,
                {fontSize: screenWidth < 768 ? 12 : 14}
              ]}>
              {I18n.t('scanToCart.saveChanges')}
            </Text>
          </TouchableOpacity>
        )}
        {zoneAccessSource === 'cart' && <TouchableOpacity
          testID="zoneAccessButton"
          onPress={() => {
            setSelectedCartId(cart.id);
            setSelectedCartName(cart.cart_name || cart.cart_id);
            setShowZoneAccessModal(true);
          }}
          style={styles.zoneAccessButton}>
          <Text style={styles.zoneAccessText}>Zone Access</Text>
        </TouchableOpacity>}
      </View>
      {errorCartId === cart.id && (
        <CartErrorPrompt
          setShowPrompt={() => {
            setErrorCartId(null);
            dispatch(fetchCarts(callBack));
          }}
        />
      )}
      {blockedCart?.id === cart.id && (
        <CartErrorPrompt
          assignedOrderCount={blockedCart.count}
          onReset={resetBlockedCart}
          setShowPrompt={() => setBlockedCart(null)}
        />
      )}
    </View>
  );

  return (
    <View style={[styles.container, {padding: screenWidth < 768 ? 10 : 18}]}>
      <UnsavedChangesBar
        visible={hasUnsavedChanges}
        saving={unsavedChanges.saving}
        onSave={unsavedChanges.handleSave}
        onDiscard={unsavedChanges.handleDiscard}
      />
      <UnsavedChangesPrompt
        visible={unsavedChanges.promptVisible}
        saving={unsavedChanges.saving}
        onSave={unsavedChanges.handleSave}
        onDiscard={unsavedChanges.handleDiscard}
        onStay={unsavedChanges.handleStay}
      />
      <Text style={[styles.header, {fontSize: screenWidth < 768 ? 20 : 24}]}>
        {I18n.t('scanToCart.cartsLabel')}
      </Text>
      <View style={styles.cardsContainer}>
        {isLoading ? (
          <ActivityIndicator size="large" color="#0000ff" />
        ) : (
          <FlatList
            data={carts}
            renderItem={renderCart}
            keyExtractor={item => item.id}
          />
        )}
        <TouchableOpacity
          testID="addCartButton"
          style={styles.addCartButton}
          onPress={addNewCart}>
          <i className="icon-plus" style={{color: '#ffff', fontSize: 25}}></i>
          <Text
            style={[
              styles.addCartText,
              {fontSize: screenWidth < 768 ? 12 : 14}
            ]}>
            {I18n.t('scanToCart.addCartLabel')}
          </Text>
        </TouchableOpacity>

        <View style={styles.zonePreferenceContainer}>
          <Text style={styles.zonePreferenceLabel}>
            {I18n.t('zoneAccess.assignmentPreference')}
          </Text>
          <View style={styles.zonePreferenceControls}>
            <TouchableOpacity
              testID="zonePreferenceButton"
              style={[
                styles.zonePreferenceButton,
                !canEditScanningPrefs && {opacity: 0.5}
              ]}
              onPress={handleZonePreferenceToggle}
              disabled={isUpdatingZonePreference || !canEditScanningPrefs}>
              {isUpdatingZonePreference ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.zonePreferenceButtonText}>
                  {zoneAccessSource === 'cart' ? I18n.t('zoneAccess.carts') : I18n.t('zoneAccess.users')}
                </Text>
              )}
            </TouchableOpacity>
            {!canEditScanningPrefs && (
              <AntDesign
                testID="zonePreferenceLockIcon"
                name="lock"
                size={16}
                color="#FFFFFF"
              />
            )}
            <TouchableOpacity
              testID="zonePreferenceTooltip"
              ref={zonePreferenceIconRef}
              onPress={handleZonePreferenceTooltipToggle}
              style={styles.zonePreferenceTooltipButton}>
              <i className="icon-info-circled" style={styles.zonePreferenceInfoIcon}></i>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <Toast
        visible={showToast}
        message={toastMessage}
        onHide={hideToast}
        colors={isToastError ? ['#d40202', '#e53935'] : undefined}
        textColor={isToastError ? '#fff' : undefined}
      />

      {showZoneAccessModal && selectedCartId && (
        <ZoneAccessModal
          isVisible={showZoneAccessModal}
          onClose={() => {
            setShowZoneAccessModal(false);
            setSelectedCartId(null);
            setSelectedCartName(null);
          }}
          entityName={selectedCartName}
          entityId={selectedCartId}
          entityType="cart"
        />
      )}

      <Modal
        transparent
        visible={showZonePreferenceTooltip}
        onRequestClose={() => setShowZonePreferenceTooltip(false)}>
        <TouchableWithoutFeedback
          testID="zonePreferenceTooltipContainer"
          onPress={() => setShowZonePreferenceTooltip(false)}>
          <View style={styles.zonePreferenceTooltipModalOverlay}>
            <LinearGradient
              locations={[0, 1]}
              colors={['#8a9daf', '#d7e1e9']}
              style={[
                styles.zonePreferenceTooltipContainer,
                {
                  position: 'absolute',
                  top: getTooltipTop(zonePreferenceIconPosition),
                  left: getTooltipLeft(zonePreferenceIconPosition, 300, screenWidth),
                  width: 300,
                  padding: 10
                }
              ]}>
              <View
                style={[
                  styles.zonePreferenceTooltipArrow,
                  {
                    top: -10,
                    left: getTooltipArrowLeft(zonePreferenceIconPosition, 300, screenWidth)
                  }
                ]}
              />
              <Text style={styles.zonePreferenceTooltipText}>
                {I18n.t('zoneAccess.assignmentPreferenceTooltip')}
              </Text>
            </LinearGradient>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

export default ScanToCartSettings;
